import { DependencyList, useCallback, useEffect, useRef } from 'react';

export const useDebounce = <F extends (...args: any[]) => any>(
  callback: F,
  delay: number,
  dependencyList?: DependencyList
): [(...args: Parameters<F>) => void, () => void] => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef<F>(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const debouncedCallback = useCallback(
    (...args: Parameters<F>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    dependencyList ? [delay, ...dependencyList] : [delay]
  );

  useEffect(() => cancel, [cancel]);

  return [debouncedCallback, cancel];
};
